import { Fragment, useEffect, useState } from 'react';
import Pagination from 'react-js-pagination';
import Loading from 'components/Loading';
import { useQrConfigurations } from './hooks/useQrConfigurations';
import { QrConfiguration } from './types';
import QrCreateModal from './QrCreateModal';
import QrEditModal from './QrEditModal';
import QrDeleteDialog from './QrDeleteDialog';
import QrImagePanel from './QrImagePanel';

const QrManagementPage = () => {
    const { items, total, page, search, loading, fetchPage, refresh } = useQrConfigurations();
    const [searchInput, setSearchInput] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [editTarget, setEditTarget] = useState<QrConfiguration | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<QrConfiguration | null>(null);
    const [imageTarget, setImageTarget] = useState<QrConfiguration | null>(null);

    useEffect(() => {
        fetchPage(1, '');
    }, [fetchPage]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchPage(1, searchInput);
    };

    return (
        <div className="card">
            <div className="card-header">
                <div className="row align-items-center">
                    <div className="col-md-4">
                        <h4 className="mb-0">QR Management</h4>
                    </div>
                    <div className="col-md-5">
                        <form className="input-group" onSubmit={handleSearch}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar por nombre..."
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                            />
                            <div className="input-group-append">
                                <button className="btn btn-outline-secondary" type="submit">
                                    <i className="fa fa-search"></i>
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="col-md-3 text-right">
                        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                            <i className="fa fa-plus mr-1"></i> Nuevo QR
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body">
                {loading ? <Loading /> : items.length === 0 ? (
                    <div className="alert alert-info">
                        {search ? `No se encontraron resultados para "${search}".` : 'No hay configuraciones QR. Crea la primera.'}
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover" width="100%">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Slug</th>
                                    <th>URL destino</th>
                                    <th>Estado</th>
                                    <th>Creado</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <Fragment key={item.id}>
                                        <tr>
                                            <td>{item.name}</td>
                                            <td><code>{item.slug}</code></td>
                                            <td><small className="text-truncate d-inline-block" style={{ maxWidth: 220 }}>{item.target_url}</small></td>
                                            <td>
                                                {item.is_active
                                                    ? <span className="badge badge-success">Activo</span>
                                                    : <span className="badge badge-secondary">Inactivo</span>
                                                }
                                            </td>
                                            <td><small>{new Date(item.created_at).toLocaleDateString()}</small></td>
                                            <td className="text-right" style={{ whiteSpace: 'nowrap' }}>
                                                <button
                                                    className="btn btn-info btn-sm mr-1"
                                                    title="Generar imagen QR"
                                                    onClick={() => setImageTarget(imageTarget?.id === item.id ? null : item)}
                                                >
                                                    <i className="fa fa-qrcode"></i>
                                                </button>
                                                <button
                                                    className="btn btn-warning btn-sm mr-1"
                                                    title="Editar"
                                                    onClick={() => setEditTarget(item)}
                                                >
                                                    <i className="fa fa-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    title="Eliminar"
                                                    onClick={() => setDeleteTarget(item)}
                                                >
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                        {imageTarget?.id === item.id && (
                                            <tr key={`${item.id}-image`}>
                                                <td colSpan={6} className="p-0">
                                                    <QrImagePanel config={item} onClose={() => setImageTarget(null)} />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                        <Pagination
                            activePage={page}
                            itemsCountPerPage={20}
                            totalItemsCount={total}
                            pageRangeDisplayed={10}
                            onChange={p => fetchPage(p, search)}
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination justify-content-center"
                        />
                    </div>
                )}
            </div>

            <QrCreateModal
                isOpen={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={() => { refresh(); }}
            />
            <QrEditModal
                isOpen={!!editTarget}
                config={editTarget}
                onClose={() => setEditTarget(null)}
                onUpdated={() => { refresh(); }}
            />
            <QrDeleteDialog
                isOpen={!!deleteTarget}
                config={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onDeleted={() => { refresh(); }}
            />
        </div>
    );
};

export default QrManagementPage;
